/**
 * YawmiModule — daily-life scheduler for AWENI.
 *
 * v1 ships the calendar-grid door:
 *   - Month-view grid (7 cols × 5–6 rows)
 *   - Each cell shows Gregorian day + Hijri day + holiday badges
 *   - Click a day → expand events list below the grid
 *   - Prev/next month nav, today highlight, RTL support
 *   - Pulls from HijriCalendar (Islamic + national holidays catalog)
 *   - Synchronous render — no network, no awaits
 *
 * v2 (future):
 *   - Habit tracker reading cross-module activity events via SDK
 *   - Wishing-card composer + RDV scheduler + Atomic Habits engine
 *   - Voice reflection (Tier 3 dispatch prompt)
 *
 * Yawmi vs Memoriz boundary (NON-NEGOTIABLE per AWENI/CLAUDE.md):
 *   No Islamic content (verses, hadith, duas, Arabic religious text) lives
 *   inside Yawmi. The calendar shows Islamic HOLIDAY DATES (Eid, Ramadan,
 *   etc.) — that's calendar metadata, not Islamic content. If a user opens
 *   an Islamic holiday cell, Yawmi may eventually link out to Memoriz/Hisni
 *   for the relevant adhkar/dua, but never embed the religious text itself.
 */

import type {
  PreviewableAweniModule,
  PreviewSnapshot,
  MountedInstance,
  ModuleRoute,
} from '../types';
import type { AweniContext } from '../../types';
import { createHijriCalendar } from '../../services/HijriCalendar.impl';
import type {
  Country,
  HijriMethod,
  ResolvedEvent,
} from '../../services/HijriCalendar';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';

const ROUTES: ModuleRoute[] = [
  { path: '/yawmi', primary: true },
  { path: '/yawmi/calendar' },
  { path: '/yawmi/habits' },
];

let mountSeq = 0;
const SNAPSHOT_CACHE_KEY = 'aweni_preview_yawmi';
const STYLE_FLAG = '__aweni_yawmi_calendar_css__';

function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

function readUserCountry(): Country {
  try {
    const raw = localStorage.getItem('aweni_pref_country');
    if (raw && /^[A-Z]{2}$/.test(raw)) return raw as Country;
  } catch { /* ignore */ }
  return 'global';
}

function injectStyles(): void {
  const w = window as unknown as Record<string, unknown>;
  if (w[STYLE_FLAG]) return;
  w[STYLE_FLAG] = true;
  const style = document.createElement('style');
  style.textContent = `
    .yawmi-shell {
      max-width: 720px;
      margin: 0 auto;
      padding: 16px 12px 80px;
      color: var(--ln-text-primary, #0A0A0A);
    }
    .yawmi-cal-header {
      display: flex; align-items: center; justify-content: space-between;
      gap: 12px; margin: 4px 0 16px 0;
    }
    .yawmi-cal-title { font-size: 1.25rem; font-weight: 700; line-height: 1.2; }
    .yawmi-cal-subtitle {
      font-size: 0.85rem; color: var(--ln-text-secondary, #555);
      margin-top: 2px;
    }
    .yawmi-cal-nav {
      display: flex; gap: 8px; align-items: center;
    }
    .yawmi-cal-btn {
      width: 40px; height: 40px;
      border-radius: var(--ln-radius-md, 12px);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.08));
      background: var(--ln-bg-card, #fff);
      color: var(--ln-text-primary, #0A0A0A);
      font-size: 1rem; font-weight: 600; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .yawmi-cal-btn:hover {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-today-btn {
      width: auto; padding: 0 16px; font-size: 0.85rem;
    }
    .yawmi-cal-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .yawmi-cal-dow {
      text-align: center;
      font-size: 0.72rem;
      font-weight: 700;
      color: var(--ln-text-secondary, #555);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 4px 0 8px 0;
    }
    .yawmi-cal-cell {
      position: relative;
      min-height: 64px;
      padding: 6px 6px 4px 6px;
      border-radius: 10px;
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.06));
      cursor: pointer;
      text-align: start;
      font: inherit;
      color: inherit;
      display: flex; flex-direction: column;
      transition: transform 80ms ease, box-shadow 80ms ease;
    }
    .yawmi-cal-cell:hover {
      box-shadow: var(--ln-shadow-sm, 0 1px 4px rgba(0,0,0,0.08));
      transform: translateY(-1px);
    }
    .yawmi-cal-cell:focus-visible {
      outline: 2px solid var(--ln-accent, #0A0A0A);
      outline-offset: 2px;
    }
    .yawmi-cal-cell--other-month {
      opacity: 0.35;
    }
    .yawmi-cal-cell--today {
      background: var(--ln-bg-attention, #FFF8E7);
      border-color: var(--ln-accent-ceremonial, #C8A96A);
      border-width: 2px;
    }
    .yawmi-cal-cell--has-events {
      border-color: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-cell--selected {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
    }
    .yawmi-cal-day-num {
      font-size: 1rem; font-weight: 700; line-height: 1;
    }
    .yawmi-cal-hijri-num {
      font-size: 0.7rem; color: var(--ln-text-secondary, #777);
      margin-top: 2px;
    }
    .yawmi-cal-cell--selected .yawmi-cal-hijri-num {
      color: var(--ln-accent-text, #fff); opacity: 0.85;
    }
    .yawmi-cal-events-row {
      margin-top: auto;
      display: flex; gap: 2px; flex-wrap: wrap;
      font-size: 0.7rem; line-height: 1;
    }
    .yawmi-cal-event-dot {
      display: inline-block;
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--ln-accent, #0A0A0A);
    }
    .yawmi-cal-event-dot--islamic { background: var(--ln-accent-ceremonial, #C8A96A); }
    .yawmi-cal-event-dot--national { background: var(--ln-accent, #0A0A0A); }
    /* ST2 Pattern D — alternate-date dot is hollow ring (less likely than primary). */
    .yawmi-cal-event-dot--alt {
      background: transparent;
      border: 1.5px solid currentColor;
      color: var(--ln-accent-ceremonial, #C8A96A);
    }
    /* ST2 Pattern D — alternate-date cell badge: small "?" in upper corner. */
    .yawmi-cal-cell--alternate {
      border-style: dashed;
      border-color: var(--ln-accent-ceremonial, #C8A96A);
    }
    .yawmi-cal-cell__alt-badge {
      position: absolute;
      top: 2px;
      inset-inline-end: 4px;
      width: 14px; height: 14px;
      line-height: 14px; text-align: center;
      font-size: 10px; font-weight: 800;
      border-radius: 50%;
      background: var(--ln-accent-ceremonial, #C8A96A);
      color: #fff;
      pointer-events: none;
    }
    /* ST2 Pattern D — explainer in the day-detail panel. */
    .yawmi-cal-alt-explainer {
      font-size: 0.82rem;
      color: var(--ln-text-secondary, #555);
      background: var(--ln-bg-attention, #FFF8E7);
      padding: 8px 10px;
      border-radius: 8px;
      margin: 6px 0 10px 0;
      line-height: 1.45;
    }
    .yawmi-cal-event-emoji { font-size: 0.85rem; }
    .yawmi-cal-day-detail {
      margin: 16px 0 0 0;
      padding: 14px 16px;
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,0.08));
      border-radius: var(--ln-radius-lg, 16px);
    }
    .yawmi-cal-day-detail h3 {
      margin: 0 0 4px 0; font-size: 1.05rem; font-weight: 700;
    }
    .yawmi-cal-day-detail .subtitle {
      font-size: 0.85rem; color: var(--ln-text-secondary, #555);
      margin: 0 0 10px 0;
    }
    .yawmi-cal-event-list {
      list-style: none; padding: 0; margin: 0;
      display: flex; flex-direction: column; gap: 8px;
    }
    .yawmi-cal-event-item {
      display: flex; gap: 10px; align-items: flex-start;
      padding: 8px 10px; border-radius: 10px;
      background: var(--ln-bg-page, #fafafa);
    }
    .yawmi-cal-event-item__icon {
      flex: 0 0 auto; font-size: 1.1rem;
    }
    .yawmi-cal-event-item__label {
      font-weight: 600; font-size: 0.92rem;
    }
    .yawmi-cal-event-item__sub {
      font-size: 0.78rem; color: var(--ln-text-secondary, #666);
      margin-top: 2px;
    }
    .yawmi-cal-empty {
      padding: 24px;
      text-align: center;
      color: var(--ln-text-secondary, #666);
      font-size: 0.92rem;
    }
    [dir="rtl"] .yawmi-cal-cell { text-align: end; }
  `;
  document.head.appendChild(style);
}

interface CalendarState {
  /** First day of the displayed month (always day 1 in local TZ). */
  cursor: Date;
  /** ISO date the user clicked (or null = today by default). */
  selectedIso: string | null;
}

export class YawmiModule implements PreviewableAweniModule {
  readonly id = 'yawmi';
  readonly routes = ROUTES;
  private container: HTMLElement | null = null;
  private currentMountId: string | null = null;
  private state: CalendarState | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.currentMountId = `yawmi_${++mountSeq}`;
    injectStyles();

    const today = new Date();
    this.state = {
      cursor: new Date(today.getFullYear(), today.getMonth(), 1),
      selectedIso: this.toIso(today),
    };
    this.render(ctx);
  }

  unmount(): void {
    if (this.container) this.container.innerHTML = '';
    this.container = null;
    this.currentMountId = null;
    this.state = null;
  }

  destroy(_instance: MountedInstance): void {
    this.unmount();
  }

  // -------------------------------------------------------------------------
  // Calendar render — synchronous, idempotent.
  // -------------------------------------------------------------------------
  private render(ctx: AweniContext): void {
    if (!this.container || !this.state) return;
    const t = ctx.i18n.t.bind(ctx.i18n);
    const isRtl = ctx.i18n.dir === 'rtl';
    const locale = ctx.i18n.locale;

    const cursor = this.state.cursor;
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthLabel = new Intl.DateTimeFormat(locale, {
      month: 'long', year: 'numeric',
    }).format(cursor);

    // Hijri reference: take the Hijri date at mid-month for stability.
    const midMonth = new Date(year, month, 15);
    const hijri = gregorianToHijri(midMonth);
    const hijriLabel = formatHijri(hijri, locale);

    // Compute calendar grid: start of week before day 1 of month → end of week after last day.
    // Use Sunday-first week. (TODO: locale-aware first-day-of-week.)
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay(); // 0 = Sun
    const gridStart = new Date(year, month, 1 - startOffset);

    const lastOfMonth = new Date(year, month + 1, 0);
    const endOffset = 6 - lastOfMonth.getDay();
    const gridEnd = new Date(year, month + 1, endOffset);

    // Fetch all events in the visible range (Islamic + national + cultural).
    const country = readUserCountry();
    const cal = createHijriCalendar({
      country,
      method: 'sighting_local' as HijriMethod,
      hijriOffset: 0,
      locale,
      includeNationalHolidays: country !== 'global',
      includeAstronomical: false,
    });
    const range = cal.getEventsInRange(gridStart, gridEnd);

    // Bucket events by ISO date for fast cell lookup.
    // ST2 Pattern D — moon-sighting events appear on BOTH primary and
    // alternate dates. Per-bucket flag `isAlternate` lets the cell renderer
    // mark the less-likely day with a `?` badge so users see the uncertainty
    // at a glance without losing the badge entirely on either date.
    interface CellEvent {
      ev: ResolvedEvent;
      isAlternate: boolean;
    }
    const eventsByDate: Record<string, CellEvent[]> = {};
    for (const ev of range.events) {
      (eventsByDate[ev.primaryDate] ||= []).push({ ev, isAlternate: false });
      if (ev.alternateDate && ev.alternateDate !== ev.primaryDate) {
        (eventsByDate[ev.alternateDate] ||= []).push({ ev, isAlternate: true });
      }
    }

    const todayIso = this.toIso(new Date());

    // Day-of-week headers (Sun, Mon, ...). Use Intl with a known Sunday.
    const dowLabels: string[] = [];
    const sunday = new Date(2024, 0, 7); // 2024-01-07 = Sunday
    const dowFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    for (let i = 0; i < 7; i++) {
      const d = new Date(sunday);
      d.setDate(sunday.getDate() + i);
      dowLabels.push(dowFmt.format(d));
    }

    // Build grid cells.
    const cells: string[] = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) {
      const iso = this.toIso(cur);
      const inMonth = cur.getMonth() === month;
      const isToday = iso === todayIso;
      const isSelected = iso === this.state.selectedIso;
      const cellEvents = eventsByDate[iso] || [];
      const hijriDay = gregorianToHijri(cur).day;

      // ST2 Pattern D: when ALL events on this cell are alternate-date
      // moon-sighting events (i.e. the user's country leans toward the
      // primary date but this day is the ±1 fallback), show the `?` badge
      // and mute the dot opacity so the eye lands on the more-likely day.
      const hasAnyEvent = cellEvents.length > 0;
      const allAlternate = hasAnyEvent && cellEvents.every((c) => c.isAlternate);
      const anyMoonSighting = cellEvents.some(
        (c) => c.ev.source.uncertaintyKind === 'moon_sighting'
      );
      const showAlternateBadge = anyMoonSighting && allAlternate;

      const classes = [
        'yawmi-cal-cell',
        !inMonth ? 'yawmi-cal-cell--other-month' : '',
        isToday ? 'yawmi-cal-cell--today' : '',
        isSelected && !isToday ? 'yawmi-cal-cell--selected' : '',
        hasAnyEvent ? 'yawmi-cal-cell--has-events' : '',
        showAlternateBadge ? 'yawmi-cal-cell--alternate' : '',
      ].filter(Boolean).join(' ');

      const dotsHtml = cellEvents.slice(0, 3).map((c) => {
        const cls = c.ev.source.kind === 'islamic_holiday'
          ? 'yawmi-cal-event-dot--islamic'
          : 'yawmi-cal-event-dot--national';
        const altCls = c.isAlternate ? ' yawmi-cal-event-dot--alt' : '';
        return `<span class="yawmi-cal-event-dot ${cls}${altCls}" aria-hidden="true"></span>`;
      }).join('');

      // Pluck the underlying ResolvedEvent[] for aria + listeners.
      const resolvedEvents = cellEvents.map((c) => c.ev);

      const altBadge = showAlternateBadge
        ? `<span class="yawmi-cal-cell__alt-badge"
                  aria-label="${escHtml(t('yawmi_cal_alt_aria') || 'Alternate moon-sighting date — primary is the previous day')}">?</span>`
        : '';

      cells.push(`
        <button type="button" class="${classes}"
                data-iso="${escHtml(iso)}"
                aria-label="${escHtml(this.cellAria(cur, resolvedEvents, locale, t, allAlternate))}">
          ${altBadge}
          <span class="yawmi-cal-day-num">${cur.getDate()}</span>
          <span class="yawmi-cal-hijri-num">${hijriDay}</span>
          <span class="yawmi-cal-events-row">${dotsHtml}</span>
        </button>
      `);
      cur.setDate(cur.getDate() + 1);
    }

    // Detail panel for the selected day.
    const selectedDate = this.state.selectedIso
      ? new Date(this.state.selectedIso + 'T12:00:00Z')
      : new Date();
    const selectedCellEvents = this.state.selectedIso
      ? (eventsByDate[this.state.selectedIso] || [])
      : [];
    const selectedEvents = selectedCellEvents.map((c) => c.ev);
    const selectedAllAlternate = selectedCellEvents.length > 0
      && selectedCellEvents.every((c) => c.isAlternate);
    const selectedLabel = new Intl.DateTimeFormat(locale, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(selectedDate);
    const selectedHijri = formatHijri(gregorianToHijri(selectedDate), locale);

    const detailHtml = this.renderDayDetail(
      selectedLabel, selectedHijri, selectedEvents, locale, t, selectedAllAlternate
    );

    this.container.innerHTML = `
      <div class="yawmi-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="yawmi-cal-header">
          <div>
            <div class="yawmi-cal-title">${escHtml(monthLabel)}</div>
            <div class="yawmi-cal-subtitle">${escHtml(hijriLabel)}</div>
          </div>
          <nav class="yawmi-cal-nav" aria-label="${escHtml(t('yawmi_cal_nav_aria') || 'Calendar navigation')}">
            <button type="button" class="yawmi-cal-btn" data-action="prev"
                    aria-label="${escHtml(t('yawmi_cal_prev') || 'Previous month')}">‹</button>
            <button type="button" class="yawmi-cal-btn yawmi-cal-today-btn" data-action="today">
              ${escHtml(t('yawmi_cal_today') || 'Today')}
            </button>
            <button type="button" class="yawmi-cal-btn" data-action="next"
                    aria-label="${escHtml(t('yawmi_cal_next') || 'Next month')}">›</button>
          </nav>
        </header>
        <ol class="yawmi-cal-grid" role="grid">
          ${dowLabels.map((d) => `<li class="yawmi-cal-dow" role="columnheader">${escHtml(d)}</li>`).join('')}
          ${cells.join('')}
        </ol>
        ${detailHtml}
      </div>
    `;

    // Wire up nav + cell click handlers.
    this.container.querySelectorAll<HTMLElement>('.yawmi-cal-nav button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-action');
        if (!this.state) return;
        if (action === 'prev') {
          this.state.cursor = new Date(year, month - 1, 1);
        } else if (action === 'next') {
          this.state.cursor = new Date(year, month + 1, 1);
        } else if (action === 'today') {
          const now = new Date();
          this.state.cursor = new Date(now.getFullYear(), now.getMonth(), 1);
          this.state.selectedIso = this.toIso(now);
        }
        this.render(ctx);
      });
    });
    this.container.querySelectorAll<HTMLElement>('.yawmi-cal-cell').forEach((cell) => {
      cell.addEventListener('click', () => {
        const iso = cell.getAttribute('data-iso');
        if (!iso || !this.state) return;
        this.state.selectedIso = iso;
        this.render(ctx);
      });
    });
  }

  private renderDayDetail(
    label: string,
    hijriLabel: string,
    events: ResolvedEvent[],
    locale: string,
    t: (k: string) => string,
    allAlternate: boolean,
  ): string {
    if (events.length === 0) {
      return `
        <section class="yawmi-cal-day-detail">
          <h3>${escHtml(label)}</h3>
          <p class="subtitle">${escHtml(hijriLabel)}</p>
          <p class="yawmi-cal-empty">${escHtml(t('yawmi_cal_no_events') || 'No events on this day.')}</p>
        </section>
      `;
    }
    const items = events.map((ev) => {
      const isIslamic = ev.source.kind === 'islamic_holiday';
      const icon = isIslamic ? '🕌' : '📅';
      const i18nName = isIslamic
        ? (t(`holiday_${ev.source.id}`) || ev.source.nameI18n[locale as 'en'] || ev.source.id)
        : (ev.source.nameI18n[locale as 'en'] || ev.source.id);
      const sub: string[] = [];
      if (ev.showAsRange && ev.alternateDate && ev.alternateDate !== ev.primaryDate) {
        sub.push(`${t('holiday_uncertainty_badge_pm1') || '±1 day'}`);
      }
      if (ev.source.isPublicHoliday) {
        sub.push(t('yawmi_cal_public_holiday') || 'Public holiday');
      }
      return `
        <li class="yawmi-cal-event-item">
          <span class="yawmi-cal-event-item__icon" aria-hidden="true">${icon}</span>
          <div>
            <div class="yawmi-cal-event-item__label">${escHtml(i18nName)}</div>
            ${sub.length ? `<div class="yawmi-cal-event-item__sub">${escHtml(sub.join(' · '))}</div>` : ''}
          </div>
        </li>
      `;
    }).join('');
    // ST2 Pattern D explainer: when this day is ALL alternate-dates of
    // moon-sighting events, surface the uncertainty model in plain words
    // so users don't think the calendar is inconsistent.
    const altExplainer = allAlternate
      ? `<p class="yawmi-cal-alt-explainer">${escHtml(t('yawmi_cal_alt_explainer') || 'These events depend on local moon-sighting. Your country typically follows the previous day; this is the alternate ±1 day.')}</p>`
      : '';
    return `
      <section class="yawmi-cal-day-detail">
        <h3>${escHtml(label)}</h3>
        <p class="subtitle">${escHtml(hijriLabel)}</p>
        ${altExplainer}
        <ul class="yawmi-cal-event-list">${items}</ul>
      </section>
    `;
  }

  private cellAria(
    d: Date,
    events: ResolvedEvent[],
    locale: string,
    t: (k: string) => string,
    allAlternate: boolean,
  ): string {
    const dateLabel = new Intl.DateTimeFormat(locale, {
      weekday: 'long', day: 'numeric', month: 'long',
    }).format(d);
    if (events.length === 0) return dateLabel;
    const eventLabel = events.length === 1
      ? events[0].source.nameI18n[locale as 'en'] || events[0].source.id
      : `${events.length} ${t('yawmi_cal_events_short') || 'events'}`;
    // ST2 Pattern D — narrate the ±1 uncertainty for screen readers.
    const altSuffix = allAlternate
      ? ` (${t('yawmi_cal_alt_aria_suffix') || 'alternate moon-sighting date — primary is the previous day'})`
      : '';
    return `${dateLabel} — ${eventLabel}${altSuffix}`;
  }

  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // -------------------------------------------------------------------------
  // preview() — used by the AWENI home Today zone. Reports today's "next
  // event" and any upcoming holidays. Stays cheap (<5ms).
  // -------------------------------------------------------------------------
  async preview(ctx: AweniContext): Promise<PreviewSnapshot> {
    try {
      const t = ctx.i18n.t.bind(ctx.i18n);
      const country = readUserCountry();
      const cal = createHijriCalendar({
        country,
        method: 'sighting_local' as HijriMethod,
        hijriOffset: 0,
        locale: ctx.i18n.locale,
        includeNationalHolidays: false,
        includeAstronomical: false,
      });
      const next = cal.getNextHoliday();

      let primaryLabel = t('yawmi_preview_today_label') || 'Today';
      let primaryValue = t('yawmi_preview_placeholder_value') || 'Daily check';
      let secondaryValue: string | undefined;
      let bgVariant: PreviewSnapshot['bgVariant'] = 'neutral';

      if (next) {
        const name = t(`holiday_${next.holiday.id}`) || next.holiday.name[ctx.i18n.locale] || next.holiday.id;
        if (next.daysAway === 0) {
          primaryLabel = name;
          primaryValue = t('holiday_today') || 'Today';
          bgVariant = 'success';
        } else if (next.daysAway === 1) {
          primaryLabel = name;
          primaryValue = t('holiday_tomorrow') || 'Tomorrow';
          bgVariant = 'attention';
        } else if (next.daysAway <= 30) {
          primaryLabel = name;
          primaryValue = `${t('holiday_in') || 'in'} ${next.daysAway} ${t('holiday_days') || 'days'}`;
          bgVariant = next.daysAway <= 7 ? 'attention' : 'neutral';
        } else {
          primaryLabel = t('yawmi_preview_today_label') || 'Today';
          primaryValue = t('yawmi_preview_placeholder_value') || 'Daily check';
          secondaryValue = `${name} ${t('holiday_in') || 'in'} ${next.daysAway} ${t('holiday_days') || 'days'}`;
        }
      }

      const snapshot: PreviewSnapshot = {
        primaryMetric: { label: primaryLabel, value: primaryValue },
        secondaryMetric: secondaryValue ? { label: '', value: secondaryValue } : undefined,
        ctaLabel: t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: `Yawmi — ${primaryLabel} ${primaryValue}`,
        bgVariant,
        computedAt: new Date().toISOString(),
      };
      try { localStorage.setItem(SNAPSHOT_CACHE_KEY, JSON.stringify(snapshot)); }
      catch { /* ignore */ }
      return snapshot;
    } catch {
      return {
        primaryMetric: { label: '', value: '' },
        ctaLabel: ctx.i18n.t('yawmi_preview_open') || 'Open Yawmi',
        ctaRoute: '#/yawmi',
        iconName: 'check-mark',
        ariaLabel: 'Yawmi',
        errorState: {
          message: ctx.i18n.t('preview_tap_to_open') || 'Tap to open',
          recoveryRoute: '#/yawmi',
        },
        computedAt: new Date().toISOString(),
      };
    }
  }
}

export default new YawmiModule();
